/**
 * fow.ts — v4M10.1 Fog of War snapshot helpers per `docs/FOW.md §3.1-§3.2`.
 *
 * This module shipping in v4M10.1 carries only the **snapshot capture**
 * primitives. The layer resolver (`currentLayerForSystem`) ships in
 * v4M10.2; renderer consumers land then. Strict-parity for v4M10.1:
 * snapshots get captured + persisted but no consumer reads them yet.
 *
 * Snapshot lifecycle per the lock:
 *  - Every tick, for every (viewer empire, scouted system) pair, refresh.
 *  - Preserved verbatim on Seen → Fog of War transition (v4M10.2 territory).
 *  - Initial state: each empire's homeworld populated immediately at
 *    scenario gen so the player has Seen-layer info from tick 0.
 *
 * Lock-language note: the v4M0.8 design lock says capture only on Seen
 * systems. v4M10.1 keeps the broader "every scouted system" trigger —
 * the Seen vs Fog of War distinction lands at v4M10.2 with the layer
 * resolver. Functionally equivalent for strict-parity: snapshots are
 * captured but never read in this checkpoint.
 */

import { areTeammates } from '../game/teams';
import { isSensorRangeGalaxyEffect } from '../game/balance-structure-effects';
import type {
  EmpireId,
  FogOfWarSnapshot,
  GameState,
  PlanetFoWState,
  StructureFoWState,
  SystemId,
} from './types/index';

/**
 * v4M10.2 — three-layer FoW resolver per `docs/FOW.md §1`. Determines
 * whether a system reads as Unexplored / Fog of War / Seen for a given
 * viewer empire on the current tick.
 *
 *   - **Unexplored**: viewer has never had a friendly source in the
 *     system (and, post-v4M10.6, no LRSA reveal covers it). Empire's
 *     `scoutedSystemIds` doesn't include the system.
 *   - **Fog of War**: viewer has visited (scoutedSystemIds.has(sysId))
 *     but no current friendly source provides live vision. Renderer
 *     reads from `state.fogOfWarSnapshots`.
 *   - **Seen**: viewer has at least one friendly source currently
 *     providing vision — a viewer-owned (or teammate-owned per
 *     `TEAMS.md §3`) ship / planet / structure in the system. Renderer
 *     reads live state.
 *
 * `devRevealAll` (F10 cheat) collapses every result to `'seen'` so QA
 * can inspect any system in full.
 */
export type SystemFoWLayer = 'unexplored' | 'fog-of-war' | 'seen';

export function currentLayerForSystem(
  state: GameState,
  viewerId: EmpireId,
  sysId: SystemId,
  devRevealAll = false,
): SystemFoWLayer {
  if (devRevealAll) return 'seen';
  const viewer = state.empires.get(viewerId);
  if (!viewer) return 'unexplored';
  // Scouted — distinguish Seen (live friendly source) from Fog of War.
  // Unscouted — fall through to the v4M10.6 LRSA-snapshot check below.
  if (viewer.scoutedSystemIds.has(sysId)) {
    // A "friendly source" is a viewer-owned or teammate-owned ship /
    // planet / structure in the system.
    for (const ship of state.ships.values()) {
      if (ship.systemId !== sysId) continue;
      if (ship.currentHull <= 0) continue;
      if (
        ship.ownerEmpireId === viewerId ||
        areTeammates(state, viewerId, ship.ownerEmpireId)
      ) {
        return 'seen';
      }
    }
    for (const planet of state.planets.values()) {
      if (planet.systemId !== sysId) continue;
      if (planet.ownerEmpireId === null) continue;
      if (
        planet.ownerEmpireId === viewerId ||
        areTeammates(state, viewerId, planet.ownerEmpireId)
      ) {
        return 'seen';
      }
    }
    // Structures are anchored to planets; the planet-ownership check
    // above already covers them. Standalone structure ownership in the
    // v1 state shape is always via a planet, so no separate loop is
    // needed.
    return 'fog-of-war';
  }
  // v4M10.6 — unscouted systems can still be Fog of War if LRSA reveal
  // has captured a snapshot. Per `SENSORS.md §3` the galaxy-view stays
  // Unexplored (renderer reads `scoutedSystemIds` directly), but
  // system-view drill-in resolves through this helper and should show
  // the snapshot. Team-vision union per `SENSORS.md §2.6 + §3.6`: walk
  // every teammate's snapshot map too, since `applyLrsaRevealsForTick`
  // captures into the LRSA owner's map only.
  if (hasFogOfWarSnapshot(state, viewerId, sysId)) return 'fog-of-war';
  for (const otherId of state.empires.keys()) {
    if (otherId === viewerId) continue;
    if (!areTeammates(state, viewerId, otherId)) continue;
    if (hasFogOfWarSnapshot(state, otherId, sysId)) return 'fog-of-war';
  }
  return 'unexplored';
}

/**
 * v4M10.6 — does empire `empireId` have an LRSA-or-scouted snapshot of
 * system `sysId`? Read-only probe used by `currentLayerForSystem` to
 * promote unscouted-but-LRSA-revealed systems out of Unexplored.
 */
function hasFogOfWarSnapshot(
  state: GameState,
  empireId: EmpireId,
  sysId: SystemId,
): boolean {
  const perEmpire = state.fogOfWarSnapshots.get(empireId);
  if (!perEmpire) return false;
  return perEmpire.has(sysId);
}

/**
 * Capture a snapshot for a single (viewer, system) pair. Walks every
 * planet in `sys.planetIds` and every structure on those planets,
 * cloning the observable state into the snapshot's planet + structure
 * maps. The viewer empire's id isn't needed for the capture itself —
 * the snapshot is keyed by (viewer, system) externally; this helper
 * only needs the source system + the current sim tick.
 *
 * Returns a fresh `FogOfWarSnapshot` — caller stores it in
 * `state.fogOfWarSnapshots`. Defensive shallow-clone of nested Maps so
 * future per-tick refreshes can mutate the live state without
 * affecting older snapshots.
 */
export function captureFogOfWarSnapshot(
  state: GameState,
  systemId: SystemId,
): FogOfWarSnapshot {
  const planets = new Map<number, PlanetFoWState>();
  const structures = new Map<number, StructureFoWState>();

  const sys = state.systems.get(systemId);
  if (!sys) {
    return { lastSeenTick: state.tick, planets, structures };
  }

  for (const pid of sys.planetIds) {
    const planet = state.planets.get(pid);
    if (!planet) continue;
    planets.set(pid, {
      ownerEmpireId: planet.ownerEmpireId,
      biome: planet.biome,
      currentPopulation: planet.currentPopulation,
      populationCap: planet.populationCap,
      federatedWith: planet.federatedWith,
      vassalOf: planet.vassalOf,
      tradingWith: [...planet.tradingWith],
      influence: new Map(planet.influence),
      structureIds: [...planet.structureIds],
      loyalty: planet.loyalty,
      lastBombardedTick: planet.lastBombardedTick,
    });
    for (const sid of planet.structureIds) {
      const structure = state.structures.get(sid);
      if (!structure) continue;
      structures.set(sid, {
        kind: structure.kind,
        level: structure.level,
        currentHull: structure.currentHull,
        planetId: pid,
      });
    }
  }

  return { lastSeenTick: state.tick, planets, structures };
}

/**
 * Per-tick capture pass — for every empire, for every system in the
 * empire's `scoutedSystemIds`, write a fresh snapshot. Runs at the end
 * of `advanceTick` (after combat / construction / bombardment) so the
 * snapshot reflects the current tick's outcome.
 *
 * Idempotent — running it on an unchanged system produces an identical
 * snapshot. Cost: O(planets + structures) per viewer per scouted
 * system per tick. Acceptable at v1 scale (~30 systems × ~5 empires);
 * profile + compress later if save bloat surfaces at playtest per
 * `docs/FOW.md §5` open question.
 */
export function captureFogOfWarSnapshotsForTick(state: GameState): void {
  for (const empire of state.empires.values()) {
    let perEmpire = state.fogOfWarSnapshots.get(empire.id);
    if (!perEmpire) {
      perEmpire = new Map<SystemId, FogOfWarSnapshot>();
      state.fogOfWarSnapshots.set(empire.id, perEmpire);
    }
    for (const sysId of empire.scoutedSystemIds) {
      perEmpire.set(sysId, captureFogOfWarSnapshot(state, sysId));
    }
  }
}

/**
 * v4M10.6 — for `empireId`, walk every empire-owned planet's LRSA
 * (`long-range-sensor-array`) structure, read its `sensorRangeGalaxy`
 * effect via the v4M10.5 type guard, and return a map of every system
 * within reach -> the LRSA level that's covering it.
 *
 * Per `docs/SENSORS.md §2.1` the radius is measured galaxy-frame
 * (Euclidean on `system.x` / `system.y` in 0..1 coords), NOT hop-
 * counted. Multiple LRSAs from the same empire covering the same
 * system don't stack (§2.8) — the map stores the **max** level across
 * covering LRSAs so future reads (e.g. UI surfacing "covered at L3")
 * have a meaningful answer.
 *
 * Excluded:
 *  - LRSAs at level 0 (still under construction — `STRUCTURES.md §2`).
 *  - LRSAs with `currentHull <= 0` (destroyed, awaiting despawn pass).
 *  - The LRSA's own parent system (handled by Seen-layer vision since
 *    the planet itself provides Seen; LRSA's reveal applies to OTHER
 *    systems per §2.1 last paragraph). Including it would harmlessly
 *    self-reveal a system the empire already has Seen on, so we still
 *    add it to the reach map for completeness — the per-tick reveal
 *    pass below skips capture when the system is already in the
 *    empire's `scoutedSystemIds` anyway.
 */
export function sensorReachForEmpire(
  state: GameState,
  empireId: EmpireId,
): Map<SystemId, number> {
  const reach = new Map<SystemId, number>();
  const empire = state.empires.get(empireId);
  if (!empire) return reach;
  for (const pid of empire.planetIds) {
    const planet = state.planets.get(pid);
    if (!planet) continue;
    const parent = state.systems.get(planet.systemId);
    if (!parent) continue;
    for (const sid of planet.structureIds) {
      const structure = state.structures.get(sid);
      if (!structure) continue;
      if (structure.kind !== 'long-range-sensor-array') continue;
      if (structure.level < 1) continue;
      if (structure.currentHull <= 0) continue;
      const row = state.structuresData.byKind[structure.kind];
      if (!row) continue;
      for (const effect of row.effects) {
        if (!isSensorRangeGalaxyEffect(effect)) continue;
        const radius = effect.rangeByLevel[structure.level - 1] ?? 0;
        if (radius <= 0) continue;
        const r2 = radius * radius;
        for (const sys of state.systems.values()) {
          const dx = sys.x - parent.x;
          const dy = sys.y - parent.y;
          if (dx * dx + dy * dy > r2) continue;
          const existing = reach.get(sys.id);
          if (existing === undefined || structure.level > existing) {
            reach.set(sys.id, structure.level);
          }
        }
      }
    }
  }
  return reach;
}

/**
 * v4M10.6 — per-tick LRSA-to-FoW reveal pass. For every empire, compute
 * sensor reach via `sensorReachForEmpire`; for every revealed system
 * that isn't already in the empire's `scoutedSystemIds`, capture a
 * snapshot into the empire's `fogOfWarSnapshots` map. Per `SENSORS.md
 * §3` the system is NOT promoted to `scoutedSystemIds` (galaxy view
 * stays Unexplored treatment — the asymmetry is load-bearing for
 * "have you actually visited this system" cueing).
 *
 * Idempotent: re-running on an unchanged state produces identical
 * snapshot contents. Designed to run AFTER `captureFogOfWarSnapshots
 * ForTick` (which handles every scouted system) so the LRSA reveal
 * only writes the additional unscouted-but-LRSA-covered systems —
 * scouted systems already have a fresh snapshot from the prior pass.
 *
 * Team-vision union is handled at READ time (`currentLayerForSystem`
 * walks every teammate's snapshot map) rather than at write time, so
 * the capture pass stays per-empire-owner. This matches the v4M10.2
 * resolver pattern where Seen-layer team-vision flows through `are
 * Teammates` checks on ship / planet ownership rather than via
 * pre-flattened team state.
 */
export function applyLrsaRevealsForTick(state: GameState): void {
  for (const empire of state.empires.values()) {
    const reach = sensorReachForEmpire(state, empire.id);
    if (reach.size === 0) continue;
    let perEmpire = state.fogOfWarSnapshots.get(empire.id);
    if (!perEmpire) {
      perEmpire = new Map<SystemId, FogOfWarSnapshot>();
      state.fogOfWarSnapshots.set(empire.id, perEmpire);
    }
    for (const sysId of reach.keys()) {
      if (empire.scoutedSystemIds.has(sysId)) continue;
      perEmpire.set(sysId, captureFogOfWarSnapshot(state, sysId));
    }
  }
}
